import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  DollarSign,
  ArrowRight,
  CreditCard,
  FileText
} from 'lucide-react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip,
  Legend,
  LineChart,
  Line
} from 'recharts';

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, chartsRes] = await Promise.all([
        api.get('/dashboard/summary'),
        api.get('/dashboard/charts'),
      ]);
      setSummary(summaryRes.data.data);
      setCharts(chartsRes.data.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Welcome back! Here's an overview of your finances.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-label text-green-700">Total Credit</p>
              <p className="stat-value text-green-600">{formatCurrency(summary?.total_credit)}</p>
            </div>
            <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="stat-card bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-label text-red-700">Total Debit</p>
              <p className="stat-value text-red-600">{formatCurrency(summary?.total_debit)}</p>
            </div>
            <div className="w-12 h-12 bg-red-200 rounded-full flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="stat-card bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-label text-yellow-700">Pending</p>
              <p className="stat-value text-yellow-600">{formatCurrency(summary?.total_pending)}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-200 rounded-full flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="stat-card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-label text-blue-700">Balance</p>
              <p className="stat-value text-blue-600">{formatCurrency(summary?.credit_balance)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts section */}
      {charts && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Credit by Type Pie Chart */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Credits by Type</h3>
            {charts.credit_pie?.labels?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={charts.credit_pie.labels.map((label, index) => ({
                        name: label,
                        value: charts.credit_pie.values[index]
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {charts.credit_pie.labels.map((_, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={charts.credit_pie.colors?.[index] || '#8884d8'} 
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No credit data available</p>
            )}
          </div>

          {/* Monthly Trends Line Chart */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Trends</h3>
            {charts.monthly_trends?.labels?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={charts.monthly_trends.labels.map((label, index) => ({
                    name: label,
                    credit: charts.monthly_trends.credit?.[index] || 0,
                    debit: charts.monthly_trends.debit?.[index] || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => formatCurrency(value)} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line type="monotone" dataKey="credit" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
                    <Line type="monotone" dataKey="debit" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No trend data available</p>
            )}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Credits */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Credits</h3>
            <Link to="/credits" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {summary?.recent_credits?.length > 0 ? (
            <div className="space-y-3">
              {summary.recent_credits.slice(0, 5).map((credit) => (
                <div key={credit.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{credit.name}</p>
                      <p className="text-sm text-gray-500">{credit.entry_type}</p>
                    </div>
                  </div>
                  <p className="font-semibold text-green-600">+{formatCurrency(credit.amount)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No recent credits</p>
          )}
        </div>

        {/* Recent Debits */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Debits</h3>
            <Link to="/debits" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {summary?.recent_debits?.length > 0 ? (
            <div className="space-y-3">
              {summary.recent_debits.slice(0, 5).map((debit) => (
                <div key={debit.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center">
                      <FileText className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{debit.beneficiary}</p>
                      <p className="text-sm text-gray-500">{debit.debit_type}</p>
                    </div>
                  </div>
                  <p className="font-semibold text-red-600">-{formatCurrency(debit.sent_money)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No recent debits</p>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Link to="/credits" className="btn btn-primary flex flex-col items-center gap-2 py-4">
            <CreditCard className="w-6 h-6" />
            Add Credit
          </Link>
          <Link to="/debits" className="btn btn-secondary flex flex-col items-center gap-2 py-4">
            <FileText className="w-6 h-6" />
            Add Debit
          </Link>
          <Link to="/reports" className="btn btn-outline flex flex-col items-center gap-2 py-4">
            <TrendingUp className="w-6 h-6" />
            View Reports
          </Link>
          <Link to="/profile" className="btn btn-outline flex flex-col items-center gap-2 py-4">
            <DollarSign className="w-6 h-6" />
            My Profile
          </Link>
        </div>
      </div>
    </div>
  );
}
